/** LIBRARY: the player's saved caves as thumbnails - load, duplicate, delete (with a confirm). */

import { buildCave, mapSize } from '../../../caves/build';
import type { SavedCave } from '../../../platform/caveLibrary';
import { BLACK, DARK_GREY, GREY, WHITE, YELLOW } from '../../../render/palette';
import { thumbnail } from '../../../render/thumbnail';
import type { KeyInput } from '../../../ui/scene';
import { copyName } from '../../model/text';
import type { KitApi } from '../api';
import type { Rect } from '../layout';
import { INK } from '../layout';
import { button, dialog, fit } from '../widgets';
import { confirmModal } from './confirm';
import type { Modal } from './modal';

const COLS = 4;
const ROWS = 2;
const PER_PAGE = COLS * ROWS;
const CARD_W = 138;
const CARD_H = 96;

export function libraryModal(k: KitApi, load: (saved: SavedCave) => void): Modal {
  const lib = k.host.services.library;
  let caves = lib.list();
  let sel = Math.max(0, caves.findIndex((c) => c.key === k.st.libraryKey));
  const refresh = (key?: string): void => {
    caves = lib.list();
    const at = key === undefined ? -1 : caves.findIndex((c) => c.key === key);
    sel = at >= 0 ? at : Math.min(sel, Math.max(0, caves.length - 1));
  };
  const current = (): SavedCave | undefined => caves[sel];
  const choose = (): void => {
    const c = current();
    if (c) load(c);
  };
  const duplicate = (): void => {
    const c = current();
    if (!c) return;
    const key = lib.save({ ...c.def, name: copyName(c.def.name) }, Date.now());
    refresh(key);
    k.sound('select');
    k.st.say(`COPIED AS ${copyName(c.def.name)}`);
  };
  const remove = (): void => {
    const c = current();
    if (!c) return;
    k.openModal(confirmModal(k, {
      title: 'DELETE A CAVE',
      lines: [`DELETE ${c.def.name} FROM YOUR LIBRARY?`, 'THIS CANNOT BE UNDONE.'],
      yes: 'DELETE', no: 'KEEP', danger: true,
      onYes: () => {
        lib.remove(c.key);
        if (k.st.libraryKey === c.key) k.st.forget();
        refresh();
        k.st.say(`DELETED ${c.def.name}`);
      },
    }));
  };
  const move = (d: number): void => {
    if (caves.length === 0) return;
    sel = Math.min(caves.length - 1, Math.max(0, sel + d));
    k.sound('move');
  };

  return {
    draw(ui) {
      const r = dialog(ui, 600, 290, `YOUR CAVES  ${caves.length}`);
      const s = ui.screen;
      if (caves.length === 0) {
        s.text('NO SAVED CAVES YET.', r.x + r.w / 2, r.y + 100, WHITE, 'center');
        s.text('BUILD ONE AND PRESS SAVE (CTRL+S).', r.x + r.w / 2, r.y + 116, INK.dim, 'center');
      }
      const page = Math.floor(sel / PER_PAGE);
      caves.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE).forEach((c, n) => {
        const i = page * PER_PAGE + n;
        const x = r.x + 12 + (n % COLS) * (CARD_W + 6);
        const y = r.y + 16 + Math.floor(n / COLS) * (CARD_H + 8);
        const id = `card${i}`;
        const on = i === sel;
        s.fill(x, y, CARD_W, CARD_H, on ? DARK_GREY : BLACK);
        const { w, h } = mapSize(c.def);
        if (w > 0 && h > 0) {
          const px = Math.min(128 / w, 70 / h);
          const img = thumbnail(buildCave(c.def, 1), px);
          s.image(img, x + (CARD_W - img.width) / 2, y + 5 + (70 - img.height) / 2);
        }
        s.text(fit(c.def.name, 16), x + 5, y + 78, on ? YELLOW : WHITE);
        s.text(`${w}X${h}`, x + 5, y + 87, GREY);
        if (c.key === k.st.libraryKey) s.text('EDITING', x + CARD_W - 5, y + 87, INK.ok, 'right');
        s.box(x, y, CARD_W, CARD_H, on ? YELLOW : ui.hot(id) ? WHITE : GREY);
        const tip = on ? 'CLICK AGAIN OR ENTER TO LOAD' : c.def.hint || c.def.name;
        ui.add({ x, y, w: CARD_W, h: CARD_H, id, tip, click: () => {
          if (on) choose();
          else {
            sel = i;
            k.sound('move');
          }
        }, wheel: (d) => move(d * COLS) });
      });
      const pages = Math.max(1, Math.ceil(caves.length / PER_PAGE));
      if (pages > 1) s.text(`PAGE ${page + 1} OF ${pages}  (WHEEL / ARROWS)`, r.x + r.w / 2, r.y + 228, GREY, 'center');
      const by = r.y + r.h - 28;
      const none = caves.length === 0;
      const at = (x: number, w = 130): Rect => ({ x: r.x + x, y: by, w, h: 18 });
      button(ui, at(12), 'LOAD (ENTER)', {
        id: 'lib-load', tip: 'EDIT IT', disabled: none, ink: INK.ok, click: choose,
      });
      button(ui, at(150), 'DUPLICATE (D)', { id: 'lib-dup', tip: 'SAVE A COPY', disabled: none, click: duplicate });
      button(ui, at(288), 'DELETE (DEL)', {
        id: 'lib-del', tip: 'REMOVE IT FROM THE LIBRARY', disabled: none, ink: INK.error, click: remove,
      });
      button(ui, at(r.w - 112, 100), 'CLOSE', { id: 'lib-close', tip: 'ESC', click: () => k.closeModal() });
    },
    key(key: KeyInput) {
      const c = key.code;
      if (c === 'ArrowLeft') move(-1);
      else if (c === 'ArrowRight') move(1);
      else if (c === 'ArrowUp') move(-COLS);
      else if (c === 'ArrowDown') move(COLS);
      else if (c === 'PageUp') move(-PER_PAGE);
      else if (c === 'PageDown') move(PER_PAGE);
      else if (c === 'Enter' || c === 'NumpadEnter' || c === 'Space') choose();
      else if (c === 'KeyD') duplicate();
      else if (c === 'Delete') remove();
      else if (c === 'Escape' || c === 'Backspace' || c === 'KeyO') k.closeModal();
    },
  };
}
